import React, { useCallback, useMemo } from 'react'
import { UIButton } from 'src/components/common/uiButton/UIButton'
import forwardIcon from '../../../assets/icons/forward-icon.svg'
import openIcon from '../../../assets/icons/open-icon.svg'
import { Flex } from 'antd'
import Tag from 'src/components/common/tag/Tag'
import styles from './TagSection.module.less'
import { ButtonConfig } from 'src/types/expert.types'
import { FilteredTag } from 'src/hooks/useTag'
import { DataTestId, TextConstants } from 'src/constants/ui.constants'
import SegmentAnalytics from 'src/SegmentAnalytics'

const { FilterBySkill, HideFilters, MoreFilters, ClearFilters } = TextConstants.TagSection

export type TagSectionProps = {
    toggleSidebar: (val: boolean) => void
    isSidebarCollapsed: boolean
    toggleFilterVisibility: () => void
    isFilterVisible: boolean
    tags: FilteredTag[]
    removeTags: (key: string, value: string) => void
    removeAllTags: () => void
}

export const buttonTagConfig = {
    filterBySkill: {
        title: FilterBySkill,
        key: 'filterBySkill',
        icon: forwardIcon,
    },
    hideFilters: {
        title: HideFilters,
        key: 'hideFilters',
        icon: openIcon,
    },
    showFilters: {
        title: MoreFilters,
        key: 'showFilters',
        icon: openIcon,
    },
}

export const TagSection: React.FC<TagSectionProps> = props => {
    const {
        isFilterVisible,
        isSidebarCollapsed,
        toggleSidebar,
        toggleFilterVisibility,
        removeAllTags,
        removeTags,
        tags,
    } = props

    const memoizedBtnTags: ButtonConfig[] = useMemo(() => {
        return [
            {
                ...buttonTagConfig.filterBySkill,
                shouldDisplay: isSidebarCollapsed,
                onClick: () => toggleSidebar(false),
            },
            {
                ...(isFilterVisible ? buttonTagConfig.hideFilters : buttonTagConfig.showFilters),
                shouldDisplay: true,
                onClick: toggleFilterVisibility,
            },
        ]
    }, [isSidebarCollapsed, isFilterVisible, toggleFilterVisibility, toggleSidebar])

    const handleTagRemove = useCallback(
        (key: string, value: string) => {
            SegmentAnalytics.sendTrackEvent(`FilterSection_${key}_Filter_Removed`, {
                filterLabel: key,
                selectedValue: value,
            })
            removeTags(key, value)
        },
        [removeTags]
    )

    const handleClearFilters = useCallback(() => {
        const selectedFilters = tags.reduce((acc: Record<string, string>, val: FilteredTag) => {
            acc[val.key] = val.value
            return acc
        }, {})

        SegmentAnalytics.sendTrackEvent(`FilterSection_ClearFiltersCTA_Clicked`, {
            selectedFilters,
        })
        removeAllTags()
    }, [removeAllTags, tags])

    return (
        <Flex
            align="flex-start"
            gap={'10px'}
            justify="flex-start"
            data-testid={DataTestId.TagSection}
        >
            {memoizedBtnTags.map(
                btn =>
                    btn.shouldDisplay && (
                        <UIButton
                            key={btn.key}
                            title={btn.title}
                            icon={btn.icon}
                            onClick={btn.onClick}
                            type="tag"
                            iconPosition="start"
                        />
                    )
            )}
            {tags.length > 0 && <div className={styles.separator}>&nbsp;</div>}
            <Flex gap={'10px'} className={styles.tagSection} wrap>
                {tags.map(tag => (
                    <Tag
                        key={`${tag.key}-${tag.value}`}
                        label={`${tag.key}${tag.value && ':'} ${tag.value}`}
                        closable
                        onClose={() => handleTagRemove(tag.key, tag.value)}
                        textEllipsis
                    />
                ))}
            </Flex>
            {tags.length > 0 && <div className={styles.separator}>&nbsp;</div>}
            {tags.length > 0 && (
                <span className={styles.clear} onClick={handleClearFilters}>
                    {ClearFilters}
                </span>
            )}
        </Flex>
    )
}
